import { I18nextPlugin, Tolgee, withTolgee } from "@tolgee/i18next";
import i18next from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import ICU from "i18next-icu";
import I18NextVue from "i18next-vue";
import type { App } from "vue";
import { SINGLE_MODIFIERS_GROUP_ID } from "#/constants/modifiers";

const I18NEXT_NS = "translation";

export const TRANSLATION_LOADERS = {
	"ar:translation": () => import("#/localization/translation/ar.json").then((m) => m.default),
	"az:translation": () => import("#/localization/translation/az.json").then((m) => m.default),
	"en:translation": () => import("#/localization/translation/en.json").then((m) => m.default),
	"es:translation": () => import("#/localization/translation/es.json").then((m) => m.default),
	"hy:translation": () => import("#/localization/translation/hy.json").then((m) => m.default),
	"ka:translation": () => import("#/localization/translation/ka.json").then((m) => m.default),
	"kk:translation": () => import("#/localization/translation/kk.json").then((m) => m.default),
	"ru:translation": () => import("#/localization/translation/ru.json").then((m) => m.default),
	"uz:translation": () => import("#/localization/translation/uz.json").then((m) => m.default),
	"zh:translation": () => import("#/localization/translation/zh.json").then((m) => m.default),
};

const tolgee = Tolgee()
	.use(I18nextPlugin())
	.init({
		apiUrl: import.meta.env.VITE_APP_TOLGEE_API_URL,
		apiKey: import.meta.env.VITE_APP_TOLGEE_API_KEY,
		staticData: TRANSLATION_LOADERS,
	});

withTolgee(i18next, tolgee)
	.use(ICU)
	.use(LanguageDetector)
	.init({
		debug: false,
		lng: localStorage.getItem("locale") || "ru",
		fallbackLng: "en",
		supportedLngs: ["en", "ru", "es", "zh", "ar", "hy", "kk", "uz", "ka", "az"],
		defaultNS: I18NEXT_NS,
		ns: [I18NEXT_NS],
		interpolation: {
			escapeValue: false,
			prefix: "{",
			suffix: "}",
		},
		detection: {
			order: ["querystring", "localStorage", "navigator"],
			caches: ["localStorage"],
			lookupQuerystring: "l",
			lookupLocalStorage: "locale",
		},
	});

const decimalFormatter = new Intl.NumberFormat("ru-RU", { style: "decimal", minimumFractionDigits: 0, maximumFractionDigits: 2, useGrouping: true });

export const getLocaleFor = (key: string, item: any, language: string) => {
	if (!item) return null;
	return item?.[`loc_${key}`]?.[language] || item?.[`loc_${key}`]?.[`${key}_${language}`] || item?.[key];
};

export const getLocaleName = (item: any, language: string) => {
	if (!item) return null;
	// @tolgee-key { key: 'card.modifiers.additions', ns: 'translation' }
	if (item.id === SINGLE_MODIFIERS_GROUP_ID) return i18next.t("card.modifiers.additions");
	return getLocaleFor("name", item, language);
};

export const getLocaleAltName = (item: any, language: string) => {
	if (!item) return null;
	return getLocaleFor("alt_name", item, language) || getLocaleName(item, language);
};

export const formatPrice = (number: number | string, symbol?: string | null) => {
	const numeric = Number(number);
	const formatted = decimalFormatter.format(Number.isFinite(numeric) ? numeric : 0);
	if (symbol) return `${formatted} ${symbol}`;
	return formatted;
};

export const formatDuration = (duration: number, _language?: string) => {
	const hours = Math.floor(duration / 60);
	const minutes = duration % 60;

	// @tolgee-key { key: 'duration.minutes', ns: 'translation' }
	if (hours === 0) return i18next.t(`duration.minutes`, { minutes });
	// @tolgee-key { key: 'duration.hours', ns: 'translation' }
	if (minutes === 0) return i18next.t(`duration.hours`, { hours });
	// @tolgee-key { key: 'duration.full', ns: 'translation' }
	return i18next.t(`duration.full`, { hours, minutes });
};

export const createI18n = <T extends App>(app: T) => {
	app.use(I18NextVue, { i18next });
	return app;
};
